import { resolveColumnEdit, type DataTableColumn, type DataTableEditConfig } from "@/components/table/columns";

/**
 * The inline-edit state machine's pure half (same split as
 * `lib/loading-phase.ts` / `use-loading-phase.ts`): every decision this
 * story's inline-edit cells make lives here, as plain functions over plain
 * data, so it is fully unit-testable without a DOM. `components/table/
 * use-inline-edits.ts` is the thin React wiring around it — real async
 * calls, real timers, real `navigator.onLine` — proven live in a browser
 * rather than by a unit test, the same division 2.2 already established.
 */

export type CellEditStatus = "saving" | "error" | "offline" | "conflict";

export interface CellEditState {
  /** What the cell shows while unsaved — the user's typed/selected value. */
  value: string;
  status: CellEditStatus;
  message?: string | undefined;
  /** Conflict only: the server's current value, for "Keep theirs". */
  conflictCurrentValue?: string | undefined;
}

export function cellKey(rowId: string, columnKey: string): string {
  return `${rowId}:${columnKey}`;
}

/**
 * What `resolveEditCommit` below hands to the caller's commit callback — a
 * bare value for the two kinds this story proves live (`text`, `select`), or
 * a named event and payload for a `"transition"`-kind column (judgement
 * call 3): a future AD-23-bound status column plugs in here without the
 * shared table changing shape. Proven only synthetically (VC-2.3-18) — no
 * real column on `/settings/users` uses `"transition"`.
 */
export type EditCommitPayload =
  | { kind: "value"; value: string }
  | { kind: "transition"; event: string; payload: unknown };

/**
 * Turns whatever the editor produced (`rawValue`, always a string — an
 * `<input>` or `<select>`'s own value) into what the commit callback
 * receives, per the column's `edit` kind. The one place "transition-shaped,
 * not a raw setter" (judgement call 3) is actually decided.
 */
export function resolveEditCommit<Row>(
  config: DataTableEditConfig<Row>,
  row: Row,
  rawValue: string,
): EditCommitPayload {
  if (config.kind === "transition") {
    const { event, payload } = config.toEvent(row, rawValue);
    return { kind: "transition", event, payload };
  }
  return { kind: "value", value: rawValue };
}

/** The outcome shape every commit callback resolves to. */
export interface EditCommitOutcome {
  ok: boolean;
  conflict?: boolean;
  message?: string;
  currentValue?: string;
}

export type CellEditAction =
  | { type: "start"; key: string; value: string }
  | { type: "succeeded"; key: string }
  | { type: "failed"; key: string; message: string | undefined }
  | { type: "conflict"; key: string; message: string | undefined; currentValue: string | undefined }
  | { type: "offline"; key: string; value: string }
  | { type: "discarded"; key: string };

export interface CellEditReduction {
  states: Record<string, CellEditState>;
  /** The single shared `aria-live="polite"` announcement for this action. */
  liveMessage: string;
}

/**
 * One pure transition: the current per-cell state map plus one action in,
 * the next map and the announcement to make out. `DESIGN.md`'s two
 * announcements (`EXPERIENCE.md` § Accessibility Floor) are decided in
 * exactly one place — here — never composed ad hoc at a call site.
 */
export function reduceCellEdit(
  states: Record<string, CellEditState>,
  action: CellEditAction,
): CellEditReduction {
  switch (action.type) {
    case "start":
      return {
        states: { ...states, [action.key]: { value: action.value, status: "saving" } },
        liveMessage: "",
      };
    case "succeeded": {
      const next = { ...states };
      delete next[action.key];
      return { states: next, liveMessage: "Saved." };
    }
    case "failed":
      return {
        states: {
          ...states,
          [action.key]: {
            value: states[action.key]?.value ?? "",
            status: "error",
            message: action.message,
          },
        },
        liveMessage: "Not saved. Retry available.",
      };
    case "conflict":
      return {
        states: {
          ...states,
          [action.key]: {
            value: states[action.key]?.value ?? "",
            status: "conflict",
            message: action.message,
            conflictCurrentValue: action.currentValue,
          },
        },
        // Distinct from the plain-failure announcement on purpose (review
        // finding): a conflicted cell renders no "Retry" control at all —
        // only the banner's "Keep mine"/"Keep theirs" — so telling a
        // screen-reader user "Retry available" here names a control that
        // does not exist.
        liveMessage: "Not saved. Someone changed this while you were editing — choose which value to keep.",
      };
    case "offline":
      return {
        states: { ...states, [action.key]: { value: action.value, status: "offline" } },
        liveMessage: "",
      };
    case "discarded": {
      const next = { ...states };
      delete next[action.key];
      return { states: next, liveMessage: "" };
    }
  }
}

/**
 * Which cells to retry the instant `online` fires (VC-2.3-09) — every cell
 * currently in `"offline"` status, and nothing sourced from persisted
 * storage: this function only ever sees what is already in memory, which is
 * what makes "no offline write queue" (VC-2.3-10) true by construction
 * rather than by promise.
 */
export function cellsPendingReconnectRetry(states: Record<string, CellEditState>): string[] {
  return Object.entries(states)
    .filter(([, state]) => state.status === "offline")
    .map(([key]) => key);
}

/** Splits a `cellKey` back into its parts, for the reconnect-retry lookup. */
export function splitCellKey(key: string): { rowId: string; columnKey: string } {
  const separator = key.indexOf(":");
  return { rowId: key.slice(0, separator), columnKey: key.slice(separator + 1) };
}

/**
 * Every editable cell in the whole grid, in row-then-column order — the one
 * place `nextEditableCell`/`previousEditableCell` below decide which cells
 * count as "editable" at all. Story 15.16: editability can now be per-row
 * (`resolveColumnEdit`, `columns.ts`), so this is computed per `(row,
 * column)` pair rather than once per column — a row whose resolved edit is
 * `undefined` for a given column (e.g. a tenant with no current lease)
 * simply contributes no entry for that column, exactly as if the whole
 * column were absent for that one row. For every column whose `edit` is
 * still the plain, static (non-function) shape every column used before
 * this story, this produces the identical column-filtered-once order the
 * previous implementation did — a real, backward-compatible extension
 * (VC-15.16-07).
 */
function flattenEditableCells<Row extends { id: string }>(
  rows: Row[],
  columns: DataTableColumn<Row>[],
): { rowId: string; columnKey: string }[] {
  const cells: { rowId: string; columnKey: string }[] = [];
  for (const row of rows) {
    for (const column of columns) {
      if (resolveColumnEdit(column, row) !== undefined) {
        cells.push({ rowId: row.id, columnKey: column.key });
      }
    }
  }
  return cells;
}

/** The next editable column in the same row, or the first editable column of
 * the next row — Tab's documented behaviour (I/O matrix). `null` when there
 * is nowhere left to go (the last editable cell of the last row). */
export function nextEditableCell<Row extends { id: string }>(
  rows: Row[],
  columns: DataTableColumn<Row>[],
  current: { rowId: string; columnKey: string },
): { rowId: string; columnKey: string } | null {
  const cells = flattenEditableCells(rows, columns);
  const index = cells.findIndex((cell) => cell.rowId === current.rowId && cell.columnKey === current.columnKey);
  if (index === -1 || index + 1 >= cells.length) {
    return null;
  }
  return cells[index + 1]!;
}

/**
 * `Shift+Tab`'s mirror of `nextEditableCell` (review finding 2: Shift+Tab
 * was moving *forward*, because nothing ever computed a backward target and
 * the same "next" call ran regardless of `event.shiftKey`). The previous
 * editable column in the same row, or the last editable column of the
 * previous row. `null` at the first editable cell of the first row.
 */
export function previousEditableCell<Row extends { id: string }>(
  rows: Row[],
  columns: DataTableColumn<Row>[],
  current: { rowId: string; columnKey: string },
): { rowId: string; columnKey: string } | null {
  const cells = flattenEditableCells(rows, columns);
  const index = cells.findIndex((cell) => cell.rowId === current.rowId && cell.columnKey === current.columnKey);
  if (index <= 0) {
    return null;
  }
  return cells[index - 1]!;
}

/**
 * The value a subsequent commit for `rowId` should treat as "current",
 * given whatever this session has already confirmed since the row was last
 * rendered (review finding 3) *and* whatever the row was most recently
 * rendered with (`fallback`, sourced from the table's own row prop, which a
 * real refetch — a sort click, a filter change, anything that re-queries the
 * list — keeps current). Both are fixed-width ISO-8601 UTC timestamp
 * strings, so a plain string comparison is a correct chronological
 * comparison with no date parsing required; this function returns whichever
 * of the two is actually later, never unconditionally preferring one over
 * the other.
 *
 * Two distinct staleness directions motivate this, both real:
 *   - A second edit to a row whose first edit already landed must use the
 *     first edit's own returned value, not the value the table happened to
 *     be rendered with — that value may already be stale by the time the
 *     second edit's request is built, and sending it produces a `409`
 *     against the user's *own* prior write, misreported as a third party's
 *     change (review finding 3, the reason `known` exists at all).
 *   - Symmetrically, `known`'s cached entry can itself go stale: someone
 *     else genuinely edits the same row, the user refetches the list (a
 *     real fetch, not a local write) and is shown the fresher value, but
 *     `known` still holds whatever this session last confirmed locally.
 *     Unconditionally preferring `known` here reproduced the exact bug this
 *     mechanism exists to prevent — a spurious `409` against a value the
 *     user is already looking at on screen. Comparing timestamps and taking
 *     the later one fixes both directions with one rule, without needing to
 *     know *why* either value is the one that's current.
 *
 * `known` is a plain, in-memory, per-session map the caller updates the
 * instant each commit's real response arrives; this function does no I/O
 * and makes no assumption about *when* it is called, which is what keeps it
 * trivially testable.
 */
export function freshestKnownValue(known: Record<string, string>, rowId: string, fallback: string): string {
  const value = known[rowId];
  if (value === undefined) {
    return fallback;
  }
  return value > fallback ? value : fallback;
}

/** What `conflictMessage` needs to build its sentence — the raw shape a
 * `409 conflict` response names structurally (`field`, `current_value`,
 * `app/api/errors.py`), plus the raw value this session itself tried to
 * commit, and the API's own prose as a fallback for the one shape that
 * names neither (a stale token with no actual field change). */
export interface ConflictMessageInput {
  field: string | undefined;
  currentValue: string | undefined;
  attemptedValue: string | undefined;
  fallbackMessage: string;
}

/**
 * The conflict banner's sentence (Story 2.3 review round 2, closing the one
 * place a raw enum value like `read_only` still reached the screen
 * verbatim — everywhere else in this story already routes through a label
 * map). Reads `field`/`currentValue` structurally, never scraping a value
 * back out of `fallbackMessage`'s prose, and relabels both the current and
 * the attempted value through `labelFor` before composing the sentence —
 * the same "what happened, both values, by name" shape the API's own
 * message already follows (VC-2.3-11/VC-2.3-12), just in words a person
 * who has never seen a raw enum value can read.
 *
 * Deliberately ignorant of what `field` or its values actually mean —
 * `labelFor` is the caller's own plain-language map (e.g. `UsersList`'s
 * `ROLE_LABELS`, only for its `role` column) — so this stays reusable by
 * any future inline-editable enum column without this module ever
 * importing an entity-specific label map itself.
 */
export function conflictMessage(
  input: ConflictMessageInput,
  labelFor: (field: string, rawValue: string) => string,
): string {
  if (input.field === undefined || input.currentValue === undefined || input.attemptedValue === undefined) {
    return input.fallbackMessage;
  }
  const currentLabel = labelFor(input.field, input.currentValue);
  const attemptedLabel = labelFor(input.field, input.attemptedValue);
  return `Someone changed this to ${currentLabel} while you were editing. Yours: ${attemptedLabel}.`;
}
